import { useEffect, useMemo, useRef, useState } from "react";
import { Download, MoreHorizontal, RefreshCw, Search } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useCompanies,
  DEFAULT_COMPANY_FILTERS,
  type CompanyFilters,
  type CompanyRow,
} from "@/hooks/useCompanyFilters";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import CompanyMembersSheet from "@/components/companies/CompanyMembersSheet";
import CompanyDetailSheet from "@/components/companies/CompanyDetailSheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const csvEscape = (v: unknown): string => {
  const s = v === null || v === undefined ? '' : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadCompaniesCsv = (rows: CompanyRow[]) => {
  const headers = ['name', 'status', 'members', 'admins', 'has_no_admin', 'email', 'website', 'is_vat_registered', 'created_at'];
  const lines = [headers.join(',')];
  for (const c of rows) {
    lines.push([
      c.name,
      c.status ?? '',
      c.member_count,
      c.admin_count,
      c.has_no_admin ? 'Yes' : 'No',
      c.email ?? '',
      c.website ?? '',
      c.is_vat_registered ? 'Yes' : c.is_vat_registered === false ? 'No' : '',
      c.created_at,
    ].map(csvEscape).join(','));
  }
  const csv = lines.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `companies-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const StatusBadge = ({ company }: { company: CompanyRow }) => {
  if (company.status === "active") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
        Active
      </span>
    );
  }
  if (company.status === "suspended") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30">
        Suspended{company.suspension_type ? ` (${company.suspension_type})` : ""}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-700 text-slate-300 border border-slate-600">
      {company.status ?? "Unknown"}
    </span>
  );
};

const SkeletonCell = ({ w = "w-20" }: { w?: string }) => (
  <div className={`h-4 ${w} rounded bg-slate-700 animate-pulse`} />
);

type FilterType =
  | "hasNoAdmin"
  | "isVatRegistered"
  | "hasWebsite"
  | "suspensionType"
  | "memberCount"
  | "createdDate";

const AVAILABLE_FILTERS = [
  { value: "hasNoAdmin", label: "No active admins" },
  { value: "isVatRegistered", label: "VAT registered" },
  { value: "hasWebsite", label: "Has website" },
  { value: "suspensionType", label: "Suspension type" },
  { value: "memberCount", label: "Member count" },
  { value: "createdDate", label: "Date created" },
];

export default function Companies() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<CompanyFilters>(() => ({
    ...DEFAULT_COMPANY_FILTERS,
    status: searchParams.get("status") ?? null,
    hasNoAdmin:
      searchParams.get("hasNoAdmin") === "true"
        ? true
        : searchParams.get("hasNoAdmin") === "false"
        ? false
        : null,
    isVatRegistered:
      searchParams.get("isVatRegistered") === "true"
        ? true
        : searchParams.get("isVatRegistered") === "false"
        ? false
        : null,
    hasWebsite:
      searchParams.get("hasWebsite") === "true"
        ? true
        : searchParams.get("hasWebsite") === "false"
        ? false
        : null,
    suspensionTypes: searchParams.get("suspensionTypes")?.split(",").filter(Boolean) ?? [],
    createdDateRanges: (() => {
      const raw = searchParams.get("createdDateRanges");
      if (!raw) return [];
      try { return JSON.parse(raw); } catch { return []; }
    })(),
    memberCountRanges: (() => {
      const raw = searchParams.get("memberCountRanges");
      if (!raw) return [];
      try { return JSON.parse(raw); } catch { return []; }
    })(),
    page: parseInt(searchParams.get("page") ?? "1", 10) || 1,
    pageSize: 50,
  }));

  useEffect(() => {
    const params: Record<string, string> = {};
    if (filters.status) params.status = filters.status;
    if (filters.hasNoAdmin !== null) params.hasNoAdmin = String(filters.hasNoAdmin);
    if (filters.isVatRegistered !== null) params.isVatRegistered = String(filters.isVatRegistered);
    if (filters.hasWebsite !== null) params.hasWebsite = String(filters.hasWebsite);
    if (filters.suspensionTypes.length) params.suspensionTypes = filters.suspensionTypes.join(",");
    if (filters.createdDateRanges.length) params.createdDateRanges = JSON.stringify(filters.createdDateRanges);
    if (filters.memberCountRanges.length) params.memberCountRanges = JSON.stringify(filters.memberCountRanges);
    if (filters.page > 1) params.page = String(filters.page);
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const updateFilters = (updates: Partial<CompanyFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...updates,
      page: "page" in updates ? updates.page ?? 1 : 1,
    }));
  };

  const clearFilters = () => setFilters(DEFAULT_COMPANY_FILTERS);

  const { companies, total, totalPages, isLoading, isFetching, error, refetch } = useCompanies(filters);

  useEffect(() => {
    const channel = supabase
      .channel('admin-companies-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'companies' }, () => {
        refetch();
        queryClient.invalidateQueries({ queryKey: ['company-counts'] });
        queryClient.invalidateQueries({ queryKey: ['company-counts-no-admin'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-metrics'] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const { data: companyCounts } = useQuery({
    queryKey: ['company-counts'],
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('companies')
        .select('status')
        .neq('id', '2c24dd37-7434-4b8a-919e-2a54fb7aae20');
      if (error) throw error;
      const rows = data ?? [];
      return {
        all: rows.length,
        active: rows.filter(r => r.status === 'active').length,
        suspended: rows.filter(r => r.status === 'suspended').length,
      };
    },
  });

  const { data: noAdminCount } = useQuery({
    queryKey: ['company-counts-no-admin'],
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('query_companies', {
        p_has_no_admin: true,
        p_page: 1,
        p_page_size: 1,
      });
      if (error) throw error;
      return ((data as unknown as { total: number } | null)?.total) ?? 0;
    },
  });

  const [search, setSearch] = useState("");
  const [activeFilterType, setActiveFilterType] = useState<FilterType | null>(null);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((c) => c.name?.toLowerCase().includes(q));
  }, [companies, search]);

  const [suspendTarget, setSuspendTarget] = useState<CompanyRow | null>(null);
  const [unsuspendTarget, setUnsuspendTarget] = useState<CompanyRow | null>(null);
  const [membersTarget, setMembersTarget] = useState<CompanyRow | null>(null);
  const [detailTarget, setDetailTarget] = useState<{ id: string; name: string } | null>(null);
  const dropdownActionRef = useRef(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const openSuspend = (c: CompanyRow) => {
    setReason("");
    setSuspendTarget(c);
  };
  const closeSuspend = () => {
    if (submitting) return;
    setSuspendTarget(null);
    setReason("");
  };
  const openUnsuspend = (c: CompanyRow) => setUnsuspendTarget(c);
  const closeUnsuspend = () => {
    if (submitting) return;
    setUnsuspendTarget(null);
  };

  const handleSuspend = async () => {
    if (!suspendTarget) return;
    const trimmed = reason.trim();
    if (!trimmed) return;
    setSubmitting(true);
    const { error: rpcError } = await supabase.rpc("suspend_company_members", {
      p_company_id: suspendTarget.id,
      p_suspension_type: "manual",
      p_suspension_reason: trimmed,
    });
    setSubmitting(false);
    if (rpcError) {
      toast.error(rpcError.message);
      return;
    }
    toast.success("Company suspended");
    setSuspendTarget(null);
    setReason("");
    queryClient.invalidateQueries({ queryKey: ["companies"] });
    queryClient.invalidateQueries({ queryKey: ['company-counts'] });
  };

  const handleUnsuspend = async () => {
    if (!unsuspendTarget) return;
    setSubmitting(true);
    const { error: rpcError } = await supabase.rpc("unsuspend_company_members", {
      p_company_id: unsuspendTarget.id,
    });
    setSubmitting(false);
    if (rpcError) {
      toast.error(rpcError.message);
      return;
    }
    toast.success("Company reactivated");
    setUnsuspendTarget(null);
    queryClient.invalidateQueries({ queryKey: ["companies"] });
    queryClient.invalidateQueries({ queryKey: ['company-counts'] });
  };

  const hasActiveFilters =
    filters.hasNoAdmin !== null ||
    filters.isVatRegistered !== null ||
    filters.hasWebsite !== null ||
    filters.suspensionTypes.length > 0 ||
    filters.createdDateRanges.length > 0 ||
    filters.memberCountRanges.length > 0;


  const renderFilterPanel = () => {
    if (!activeFilterType) return null;
    const closeBtn = (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setActiveFilterType(null)}
        className="h-7 text-slate-300 hover:bg-slate-700"
      >
        Close
      </Button>
    );

    if (
      activeFilterType === "hasNoAdmin" ||
      activeFilterType === "isVatRegistered" ||
      activeFilterType === "hasWebsite"
    ) {
      const labelMap = {
        hasNoAdmin: "No active admins",
        isVatRegistered: "VAT registered",
        hasWebsite: "Has website",
      } as const;
      const current =
        activeFilterType === "hasNoAdmin"
          ? filters.hasNoAdmin
          : activeFilterType === "isVatRegistered"
          ? filters.isVatRegistered
          : filters.hasWebsite;
      const apply = (v: boolean) => {
        updateFilters({ [activeFilterType]: v } as Partial<CompanyFilters>);
        setActiveFilterType(null);
      };
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase text-slate-400">{labelMap[activeFilterType]}</div>
            {closeBtn}
          </div>
          <div className="flex gap-4">
            {[true, false].map((v) => (
              <label key={String(v)} className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                <input
                  type="radio"
                  name={activeFilterType}
                  checked={current === v}
                  onChange={() => apply(v)}
                />
                {v ? "Yes" : "No"}
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (activeFilterType === "suspensionType") {
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase text-slate-400">Suspension type</div>
            {closeBtn}
          </div>
          <div className="flex gap-4">
            {["manual", "billing"].map((v) => {
              const checked = filters.suspensionTypes.includes(v);
              return (
                <label key={v} className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(c) => {
                      const next = new Set(filters.suspensionTypes);
                      if (c) next.add(v);
                      else next.delete(v);
                      updateFilters({ suspensionTypes: Array.from(next) });
                    }}
                  />
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </label>
              );
            })}
          </div>
        </div>
      );
    }

    if (activeFilterType === "memberCount") {
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase text-slate-400">Member count (any range matches)</div>
            {closeBtn}
          </div>
          <div className="space-y-2">
            {filters.memberCountRanges.map((r, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  type="number"
                  value={r.min ?? ""}
                  onChange={(e) => {
                    const v = e.target.value === "" ? null : parseInt(e.target.value, 10);
                    updateFilters({
                      memberCountRanges: filters.memberCountRanges.map((x, j) => j === i ? { ...x, min: v } : x),
                    });
                  }}
                  placeholder="Min"
                  className="h-9 w-[120px] bg-slate-900 border-slate-700 text-slate-100"
                />
                <span className="text-xs text-slate-500">to</span>
                <Input
                  type="number"
                  value={r.max ?? ""}
                  onChange={(e) => {
                    const v = e.target.value === "" ? null : parseInt(e.target.value, 10);
                    updateFilters({
                      memberCountRanges: filters.memberCountRanges.map((x, j) => j === i ? { ...x, max: v } : x),
                    });
                  }}
                  placeholder="Max"
                  className="h-9 w-[120px] bg-slate-900 border-slate-700 text-slate-100"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => updateFilters({
                    memberCountRanges: filters.memberCountRanges.filter((_, j) => j !== i),
                  })}
                  className="h-9 text-slate-400 hover:text-white"
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() => updateFilters({
                memberCountRanges: [...filters.memberCountRanges, { min: null, max: null }],
              })}
              className="h-9 border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
            >
              + Add range
            </Button>
          </div>
        </div>
      );
    }

    if (activeFilterType === "createdDate") {
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase text-slate-400">Date created (any range matches)</div>
            {closeBtn}
          </div>
          <div className="space-y-2">
            {filters.createdDateRanges.map((r, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  type="date"
                  value={r.from ?? ""}
                  onChange={(e) => updateFilters({
                    createdDateRanges: filters.createdDateRanges.map((x, j) => j === i ? { ...x, from: e.target.value || null } : x),
                  })}
                  className="h-9 w-[160px] bg-slate-900 border-slate-700 text-slate-100"
                />
                <span className="text-xs text-slate-500">to</span>
                <Input
                  type="date"
                  value={r.to ?? ""}
                  onChange={(e) => updateFilters({
                    createdDateRanges: filters.createdDateRanges.map((x, j) => j === i ? { ...x, to: e.target.value || null } : x),
                  })}
                  className="h-9 w-[160px] bg-slate-900 border-slate-700 text-slate-100"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => updateFilters({
                    createdDateRanges: filters.createdDateRanges.filter((_, j) => j !== i),
                  })}
                  className="h-9 text-slate-400 hover:text-white"
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() => updateFilters({
                createdDateRanges: [...filters.createdDateRanges, { from: null, to: null }],
              })}
              className="h-9 border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
            >
              + Add range
            </Button>
          </div>
        </div>
      );
    }

    return null;
  };

  const statusTabValue: "all" | "active" | "suspended" | "no-admins" =
    filters.hasNoAdmin === true
      ? "no-admins"
      : filters.status === "active"
      ? "active"
      : filters.status === "suspended"
      ? "suspended"
      : "all";

  return (
    <>
      <div className="space-y-4">
        {/* Status tabs */}
        <Tabs
          value={statusTabValue}
          onValueChange={(v) => {
            if (v === "no-admins") {
              updateFilters({ status: null, hasNoAdmin: true });
            } else {
              updateFilters({ status: v === "all" ? null : v, hasNoAdmin: null });
            }
          }}
        >
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="all" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
              All{companyCounts ? ` (${companyCounts.all})` : ''}
            </TabsTrigger>
            <TabsTrigger value="active" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
              Active{companyCounts ? ` (${companyCounts.active})` : ''}
            </TabsTrigger>
            <TabsTrigger value="suspended" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
              Suspended{companyCounts ? ` (${companyCounts.suspended})` : ''}
            </TabsTrigger>
            <TabsTrigger value="no-admins" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
              No Admins{noAdminCount !== undefined ? ` (${noAdminCount})` : ''}
            </TabsTrigger>
          </TabsList>
        </Tabs>


        {/* Toolbar */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 max-w-md space-y-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search current page by company name…"
                className="pl-8 bg-slate-900 border-slate-700 text-slate-100 placeholder:text-slate-500"
              />
            </div>
            {!isLoading && !error && (
              <div className="text-xs text-slate-500">
                Showing {visible.length} of {total} companies
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center justify-center h-9 w-9 rounded border border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-300 disabled:opacity-50"
            aria-label="Refresh companies"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          </button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => downloadCompaniesCsv(visible)}
            disabled={visible.length === 0}
            className="h-9 border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
          >
            <Download className="h-3.5 w-3.5 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Filter pill bar */}
        <div className="flex flex-wrap items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="h-9 border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
              >
                + Add filter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-slate-900 border-slate-700 text-slate-100">
              {AVAILABLE_FILTERS.map((f) => (
                <DropdownMenuItem
                  key={f.value}
                  onClick={() => setActiveFilterType(f.value as FilterType)}
                  className="focus:bg-slate-800 focus:text-white"
                >
                  {f.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {filters.hasNoAdmin !== null && (
            <span
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-slate-700 text-slate-200 border border-slate-600 cursor-pointer hover:bg-slate-600"
              onClick={() => setActiveFilterType(activeFilterType === "hasNoAdmin" ? null : "hasNoAdmin")}
            >
              No admins: {filters.hasNoAdmin ? "Yes" : "No"}
              <button onClick={(e) => { e.stopPropagation(); updateFilters({ hasNoAdmin: null }); }} className="ml-1 text-slate-400 hover:text-white">×</button>
            </span>
          )}
          {filters.isVatRegistered !== null && (
            <span
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-slate-700 text-slate-200 border border-slate-600 cursor-pointer hover:bg-slate-600"
              onClick={() => setActiveFilterType(activeFilterType === "isVatRegistered" ? null : "isVatRegistered")}
            >
              VAT registered: {filters.isVatRegistered ? "Yes" : "No"}
              <button onClick={(e) => { e.stopPropagation(); updateFilters({ isVatRegistered: null }); }} className="ml-1 text-slate-400 hover:text-white">×</button>
            </span>
          )}
          {filters.hasWebsite !== null && (
            <span
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-slate-700 text-slate-200 border border-slate-600 cursor-pointer hover:bg-slate-600"
              onClick={() => setActiveFilterType(activeFilterType === "hasWebsite" ? null : "hasWebsite")}
            >
              Has website: {filters.hasWebsite ? "Yes" : "No"}
              <button onClick={(e) => { e.stopPropagation(); updateFilters({ hasWebsite: null }); }} className="ml-1 text-slate-400 hover:text-white">×</button>
            </span>
          )}
          {filters.suspensionTypes.length > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-slate-700 text-slate-200 border border-slate-600 cursor-pointer hover:bg-slate-600"
              onClick={() => setActiveFilterType(activeFilterType === "suspensionType" ? null : "suspensionType")}
            >
              Suspension type: {filters.suspensionTypes.join(", ")}
              <button onClick={(e) => { e.stopPropagation(); updateFilters({ suspensionTypes: [] }); }} className="ml-1 text-slate-400 hover:text-white">×</button>
            </span>
          )}
          {filters.memberCountRanges.map((r, i) => {
            const label = r.min !== null && r.max !== null
              ? `Members: ${r.min} to ${r.max}`
              : r.min !== null
              ? `Members: at least ${r.min}`
              : r.max !== null
              ? `Members: at most ${r.max}`
              : `Members: any`;
            return (
              <span
                key={`mc-${i}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-slate-700 text-slate-200 border border-slate-600 cursor-pointer hover:bg-slate-600"
                onClick={() => setActiveFilterType(activeFilterType === "memberCount" ? null : "memberCount")}
              >
                {label}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    updateFilters({ memberCountRanges: filters.memberCountRanges.filter((_, j) => j !== i) });
                  }}
                  className="ml-1 text-slate-400 hover:text-white"
                >×</button>
              </span>
            );
          })}
          {filters.createdDateRanges.map((r, i) => {
            const label = r.from && r.to
              ? `Created: ${r.from} to ${r.to}`
              : r.from
              ? `Created: from ${r.from}`
              : r.to
              ? `Created: until ${r.to}`
              : `Created: any`;
            return (
              <span
                key={`cd-${i}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-slate-700 text-slate-200 border border-slate-600 cursor-pointer hover:bg-slate-600"
                onClick={() => setActiveFilterType(activeFilterType === "createdDate" ? null : "createdDate")}
              >
                {label}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    updateFilters({ createdDateRanges: filters.createdDateRanges.filter((_, j) => j !== i) });
                  }}
                  className="ml-1 text-slate-400 hover:text-white"
                >×</button>
              </span>
            );
          })}
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-xs text-slate-400 hover:text-white underline">
              Clear all
            </button>
          )}
        </div>

        {renderFilterPanel()}

        {/* Table */}
        <div className="border border-slate-800 rounded-md overflow-hidden bg-slate-900">
          <table className="w-full text-sm">
            <thead className="bg-slate-800 text-slate-400 text-xs uppercase">
              <tr>
                <th className="text-left font-semibold px-4 py-2.5">Company Name</th>
                <th className="text-left font-semibold px-4 py-2.5">Status</th>
                <th className="text-left font-semibold px-4 py-2.5">Members</th>
                <th className="text-left font-semibold px-4 py-2.5">Admins</th>
                <th className="text-left font-semibold px-4 py-2.5">Created</th>
                <th className="text-right font-semibold px-4 py-2.5 w-12">Actions</th>
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12">
                    <div className="flex flex-col items-center gap-3 text-center">
                      <p className="text-sm text-red-400">{error}</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => refetch()}
                        className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
                      >
                        Retry
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-800">
                    <td className="px-4 py-3"><SkeletonCell w="w-40" /></td>
                    <td className="px-4 py-3"><SkeletonCell w="w-20" /></td>
                    <td className="px-4 py-3"><SkeletonCell w="w-8" /></td>
                    <td className="px-4 py-3"><SkeletonCell w="w-8" /></td>
                    <td className="px-4 py-3"><SkeletonCell w="w-24" /></td>
                    <td className="px-4 py-3"><SkeletonCell w="w-6" /></td>
                  </tr>
                ))
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-500">
                    No companies match your search.
                  </td>
                </tr>
              ) : (
                visible.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => {
                      if (dropdownActionRef.current) {
                        dropdownActionRef.current = false;
                        return;
                      }
                      setDetailTarget({ id: c.id, name: c.name });
                    }}
                    className="border-b border-slate-800 last:border-0 hover:bg-slate-800/50 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white">{c.name}</span>
                        {c.has_no_admin && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-red-500/15 text-red-400 border border-red-500/30">
                            No admins
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge company={c} /></td>
                    <td className="px-4 py-3 text-slate-300">{c.member_count}</td>
                    <td className={`px-4 py-3 ${c.admin_count === 0 ? "text-red-400 font-semibold" : "text-slate-300"}`}>
                      {c.admin_count}
                    </td>
                    <td className="px-4 py-3 text-slate-400">{formatDate(c.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center h-7 w-7 rounded text-slate-400 hover:bg-slate-700 hover:text-white"
                            aria-label="Open actions menu"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="bg-slate-800 border-slate-700 text-slate-100"
                        >
                          {c.status === "active" && (
                            <DropdownMenuItem
                              onClick={() => { dropdownActionRef.current = true; openSuspend(c); }}
                              className="text-amber-400 focus:bg-slate-700 focus:text-amber-300"
                            >
                              Suspend Company
                            </DropdownMenuItem>
                          )}
                          {c.status === "suspended" && (
                            <DropdownMenuItem
                              onClick={() => { dropdownActionRef.current = true; openUnsuspend(c); }}
                              className="text-emerald-400 focus:bg-slate-700 focus:text-emerald-300"
                            >
                              Unsuspend Company
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => { dropdownActionRef.current = true; setMembersTarget(c); }}
                            className="focus:bg-slate-700"
                          >
                            View Members
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && !error && total > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">{total} total companies</span>
            <div className="flex items-center gap-3">
              <Button
                size="sm"
                variant="outline"
                disabled={filters.page <= 1}
                onClick={() => updateFilters({ page: filters.page - 1 })}
                className="h-7 border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"
              >
                Previous
              </Button>
              <span className="text-xs text-slate-400">
                Page {filters.page} of {totalPages}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={filters.page >= totalPages}
                onClick={() => updateFilters({ page: filters.page + 1 })}
                className="h-7 border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      <CompanyDetailSheet
        companyId={detailTarget?.id ?? null}
        companyName={detailTarget?.name ?? null}
        open={!!detailTarget}
        onOpenChange={(open) => { if (!open) setDetailTarget(null); }}
      />

      {/* Suspend dialog */}
      <Dialog
        open={!!suspendTarget}
        onOpenChange={(open) => { if (!open) closeSuspend(); }}
      >
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-white">
              Suspend {suspendTarget?.name}?
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Suspending this company will immediately block access for all{" "}
              {suspendTarget?.member_count ?? 0} members. They will see a suspended
              account screen until the company is reactivated.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="suspend-reason" className="text-slate-300">
              Reason for suspension
            </Label>
            <Input
              id="suspend-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you suspending this company?"
              className="bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeSuspend}
              disabled={submitting}
              className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleSuspend}
              disabled={submitting || !reason.trim()}
            >
              {submitting ? "Suspending…" : "Suspend Company"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsuspend dialog */}
      <Dialog
        open={!!unsuspendTarget}
        onOpenChange={(open) => { if (!open) closeUnsuspend(); }}
      >
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-white">
              Unsuspend {unsuspendTarget?.name}?
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This will restore access for all members who were suspended when the
              company was suspended.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeUnsuspend}
              disabled={submitting}
              className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUnsuspend}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              {submitting ? "Reactivating…" : "Unsuspend"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Members sheet */}
      <CompanyMembersSheet
        company={membersTarget}
        open={!!membersTarget}
        onOpenChange={(open) => { if (!open) setMembersTarget(null); }}
      />
    </>
  );
}
